import React, {useRef} from 'react';
import ReactDOM from 'react-dom';
import {useSelector} from 'react-redux';
import colours from 'styles/colours';
import {applicationsSelector} from 'modules/applications/selectors';

import {useEscapeKey, useFocusReturn, useFocusTrap} from './hooks';
import PanelHeader from './PanelHeader';
import AppList from './AppList';
import SignOutButton from './SignOutButton';

const PANEL_WIDTH = 300;
const TRANSITION = '0.25s cubic-bezier(0.4, 0, 0.2, 1)';

const backdropStyles = (open) => ({
    position: 'fixed',
    inset: 0,
    background: colours.background.modalScrim,
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    zIndex: 300,
    opacity: open ? 1 : 0,
    pointerEvents: open ? 'auto' : 'none',
    transition: `opacity ${TRANSITION}`,
});

const drawerStyles = (open) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: PANEL_WIDTH,
    background: colours.background.panel,
    boxShadow: colours.shadow.drawer,
    zIndex: 301,
    display: 'flex',
    flexDirection: 'column',
    transform: open ? 'translateX(0)' : `translateX(-${PANEL_WIDTH + 20}px)`,
    transition: `transform ${TRANSITION}`,
    overflow: 'hidden',
    pointerEvents: open ? 'auto' : 'none',
    visibility: open ? 'visible' : 'hidden',
});

export default function SidePanel({open, onClose, triggerRef}) {
    const apps = useSelector(applicationsSelector);
    const panelRef = useRef(null);
    const closeButtonRef = useRef(null);
    const wasOpenRef = useRef(false);

    useEscapeKey(open, onClose);
    useFocusTrap(open, panelRef);
    useFocusReturn(open, triggerRef, wasOpenRef);

    React.useEffect(() => {
        if (open) {
            closeButtonRef.current?.focus();
        }
    }, [open]);

    return ReactDOM.createPortal(
        <>
            <div aria-hidden="true" onClick={onClose} style={backdropStyles(open)}/>
            <div
                ref={panelRef}
                role="dialog"
                aria-modal={open ? 'true' : undefined}
                aria-hidden={!open}
                aria-labelledby="side-panel-title"
                style={drawerStyles(open)}
            >
                <PanelHeader onClose={onClose} closeButtonRef={closeButtonRef}/>
                <AppList apps={apps} onClose={onClose}/>
                <SignOutButton onClose={onClose}/>
            </div>
        </>,
        document.body
    );
}
